import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import {
  AssettypeViewModel,
} from '../device-setting-edit-dialog/device-setting-edit-dialog.component';

import { Role } from '../device-setting-list.component';
import {
  ADMIN_TABLE_COLUMN_LIST,
  GENERAL_TABLE_COLUMN_LIST,
  SYSTEM_ADMIN_TABLE_COLUMN_LIST,
} from './device-group-list-table';
import { FilterService } from '@services/filter.service';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DeviceSettingBase } from '@utils/device-setting.util';

/** APIから返却される一括設定ステータスを表す型。 */
type BulkSettingStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED'
  | null
  | undefined;

/** フィルタとソートで利用可能な一括設定ステータス型。 */
type BulkSettingStatusFilter = Exclude<BulkSettingStatus, null | undefined>;

/** 一括設定ステータスのフィルタ選択肢。 */
const BULK_SETTING_STATUS_OPTIONS: Array<{
  value: BulkSettingStatusFilter;
  label: string;
}> = [
  { value: 'PENDING', label: '未設定' },
  { value: 'IN_PROGRESS', label: '設定中' },
  { value: 'COMPLETED', label: '設定済' },
  { value: 'FAILED', label: '設定エラー' },
];

/** 一括設定ステータスごとのソート優先順位。 */
const BULK_SETTING_STATUS_SORT_ORDER: Record<BulkSettingStatusFilter, number> =
  {
    PENDING: 0,
    IN_PROGRESS: 1,
    COMPLETED: 2,
    FAILED: 3,
  };

/** デバイス設定のViewModel */
export type DeviceViewModel = {
  /** デバイス名 */
  deviceName: string;
  /** シリアルナンバー */
  serialNo: string;
  /** デバイスタイプ */
  deviceType: string;
  /** 最終通信日時 */
  lastCommunicationAt: Date;
  /** ステータス */
  status: string;
  /** バッテリー */
  battery: string;
  /** 設定グループ */
  settingGroupName: string;
  /** タグ */
  tag: string;
};

/** デバイスグループのViewModel */
export type DeviceSettingGroupViewModel = {
  /** デバイスグループID */
  deviceSettingGroupId: string;
  /** デバイスグループ名 */
  deviceSettingGroupName: string;
  /** デバイス種類ID */
  deviceTypeId: string;
  deviceTypeKey?: string;
  deviceTypeDisplayName?: string;
  /** グループ説明 */
  description: string;
  /** 最終更新日時 */
  lastUpdatedAt: Date;
  /** 一括設定ステータス */
  bulkSettingStatus?: BulkSettingStatus;
  /** 一括設定進捗 */
  bulkSettingProgress?: number | null;
  /** 一括設定時のデバイス別エラー一覧 */
  bulkSettingErrorList?: BulkSettingErrorViewModel[] | null;
  /** 所属デバイス数 */
  deviceCount?: number;
  /** 契約期間内のデバイスに対してグループの一括設定が適用済みか */
  isAllContractDevicesBulkApplied?: boolean;
};

/** デバイス別一括設定エラーのViewModel */
export type BulkSettingErrorViewModel = {
  deviceId: string;
  errorMessage: string;
};

/** デバイスのViewModel */
export type DeviceGroupSettingDetailViewModel = DeviceSettingBase & {
  /** デバイスグループID */
  deviceSettingGroupId: string;
  /** デバイスグループ名 */
  deviceSettingGroupName: string;
  /** デバイス種類ID */
  deviceTypeId?: string;
  /** センサーデータ収集間隔 */
  samplingFrequency: number;
  /** クラウドへのデータ送信間隔 */
  reportingFrequency: number;
  /** フライトモード */
  fmd?: boolean;
  /** 全デバイスが契約終了 */
  allExpired: boolean;
};

interface DeviceGroupListFilter {
  deviceSettingGroupName?: string;
  deviceTypeDisplayName?: string;
  description?: string;
  bulkSettingStatus?: BulkSettingStatusFilter | null;
  sortState?: Sort;
  pageIndex?: number;
}

const pageId = 'DeviceGroupList';

@Component({
  selector: 'app-device-group-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    DatePipe,
    DirectivesModule,
    MatTooltipModule,
  ],
  templateUrl: './device-group-list-table.component.html',
  styleUrl: './device-group-list-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceGroupListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  /** デバイス設定詳細 */
  @Input({ required: true }) deviceSetting: unknown;

  @Input({ required: true })
  deviceSettingGroupList: DeviceSettingGroupViewModel[] = [];

  /** 権限 */
  @Input({ required: true }) role: Role = 'system_admin';
  @Input() assettypeList: AssettypeViewModel[] = [];

  /** テナント一覧 */
  @Input() tenantList: string[] = [];

  /** 現在のテナント */
  @Input() currentTenant: string = '';

  /** デバイス種類表示名一覧 */
  @Input() groupDeviceTypeDisplayNames: string[] = [];

  // /** 新規登録を通知 */
  // @Output() created = new EventEmitter<DeviceViewModel[]>();

  /** グループ詳細設定更新を通知 */
  @Output() updatedGroupDetail = new EventEmitter<DeviceSettingGroupViewModel>();

  /** グループ設定更新を通知 */
  @Output() updatedGroup = new EventEmitter<DeviceSettingGroupViewModel>();

  /** グループ設定削除を通知 */
  @Output() deletedGroup = new EventEmitter<DeviceSettingGroupViewModel>();

  @Output() openDeviceEditDialog = new EventEmitter<void>();

  @Output() openBulkSettingErrorDialog =
    new EventEmitter<DeviceSettingGroupViewModel>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** カラム */
  public displayedColumns: string[] = ADMIN_TABLE_COLUMN_LIST;

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<DeviceSettingGroupViewModel>([]);

  /** デバイスグループ名検索キーワード */
  public deviceSettingGroupNameKeyword = '';

  /** デバイス設定グループ説明検索キーワード */
  public descriptionKeyword = '';

  /** 選択されたデバイス種類 */
  public selectedDeviceType: string = '';

  /** 選択されたステータス */
  public selectedBulkSettingStatus: BulkSettingStatusFilter | null = null;

  /** ステータスフィルタ選択肢 */
  public readonly bulkSettingStatusOptions = BULK_SETTING_STATUS_OPTIONS;

  /** 初期ソート値 */
  public initSortState: Sort = {
    active: 'deviceSettingGroupName',
    direction: 'asc',
  };

  /** ソート状態 */
  private sortState: Sort | undefined;

  /** ページ番号 */
  private pageIndex: number | undefined;

  constructor(
    private filterService: FilterService<
      DeviceGroupListFilter,
      DeviceSettingGroupViewModel
    >,
    private cdr: ChangeDetectorRef,
  ) {
    this.filterService.setSessionStorageKey(pageId);
  }

  public ngOnInit(): void {
    this._setDisplayedColumns(this.role);

    // フィルター条件の取得と適用
    this.loadFilters();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // ソート設定
    this.dataSource.sortingDataAccessor = (
      item: DeviceSettingGroupViewModel,
      property: string,
    ) => {
      if (property === 'bulkSettingStatus') {
        return this.getBulkSettingStatusSortValue(item);
      }
      if (property === 'deviceTypeId') {
        return item.deviceTypeDisplayName || '';
      }

      const value = item[property as keyof DeviceSettingGroupViewModel];

      return value == null ? '' : `${value}`;
    };

    // ソートとページングの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort, this.paginator);

    // ソートとページングの設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };

      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // ページングを設定
    if (this.paginator && this.pageIndex) {
      this.setPaginationIndex(this.pageIndex);
    }

    // フィルター設定
    this.dataSource.filterPredicate = (
      element: DeviceSettingGroupViewModel,
    ) => {
      let deviceSettingGroupName = true;
      if (this.deviceSettingGroupNameKeyword) {
        // deviceSettingGroupNameKeywordが空でない場合のみフィルタリングを行う
        deviceSettingGroupName =
          element.deviceSettingGroupName
            .trim()
            .toLowerCase()
            .indexOf(this.deviceSettingGroupNameKeyword.toLowerCase()) > -1;
      }

      let deviceTypeDisplayName = true;
      if (this.selectedDeviceType) {
        deviceTypeDisplayName =
          element.deviceTypeDisplayName === this.selectedDeviceType;
      }

      let description = true;
      if (this.descriptionKeyword) {
        // descriptionKeywordが空でない場合のみフィルタリングを行う
        description =
          element.description
            .trim()
            .toLowerCase()
            .indexOf(this.descriptionKeyword.toLowerCase()) > -1;
      }

      let bulkSettingStatus = true;
      if (this.selectedBulkSettingStatus) {
        bulkSettingStatus =
          this.getNormalizedBulkSettingStatus(element) ===
          this.selectedBulkSettingStatus;
      }
      // フィルター条件を保持
      this.saveFiltersToStorage();

      return (
        deviceSettingGroupName &&
        deviceTypeDisplayName &&
        description &&
        bulkSettingStatus
      );
    };

    this.applyFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['deviceSettingGroupList'] !== undefined) {
      const deviceSettingGroupList: DeviceSettingGroupViewModel[] =
        changes['deviceSettingGroupList'].currentValue;
      this.dataSource.data = deviceSettingGroupList;

      this.setPaginationIndex(this.pageIndex);
    } else if (changes['role'] !== undefined) {
      const role: Role = changes['role'].currentValue;
      this._setDisplayedColumns(role);
    }
  }

  /**
   * 権限ごとにテーブルカラムを設定する
   * @param role 権限
   */
  private _setDisplayedColumns(role: Role) {
    if (role === 'system_admin') {
      this.displayedColumns = SYSTEM_ADMIN_TABLE_COLUMN_LIST;
    } else if (role === 'tenant_admin') {
      this.displayedColumns = ADMIN_TABLE_COLUMN_LIST;
    } else {
      this.displayedColumns = GENERAL_TABLE_COLUMN_LIST;
    }
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * 設定グループ名でのフィルタリングをクリアする。
   */
  public clearDeviceSettingGroupNameFilter(): void {
    // フィルタ条件をクリア
    this.deviceSettingGroupNameKeyword = '';

    this.applyFilter();
  }

  /**
   * グループ説明でのフィルタリングをクリアする。
   */
  public clearDescriptionFilter(): void {
    // フィルタ条件をクリア
    this.descriptionKeyword = '';

    this.applyFilter();
  }

  /**
   * 各フィルターを初期化する。
   */
  public clearFilters() {
    // フィルターを初期化
    this.loadFilters(true);

    const sortState: Sort = this.initSortState;

    this.filterService.setSort(this.dataSource, this.sort, sortState);

    this.setPaginationIndex();

    // フィルターを適用
    this.dataSource.filter = '';
    this.applyFilter();
  }

  /**
   * ページネーションの番号を設定する
   */
  private setPaginationIndex(pageIndex: number = 0) {
    if (this.pageIndex) {
      setTimeout(() => {
        this.setPageIndex(pageIndex);
      }, 50);
      this.setPageIndex(pageIndex);
    }
  }

  private setPageIndex(pageIndex: number) {
    pageIndex = this.filterService.getValidPageIndex(this.paginator, pageIndex);

    this.paginator.pageIndex = pageIndex!;
    this.cdr.detectChanges();
  }

  /**
   * フィルターをセッションストレージから読み込む。
   */
  private loadFilters(reset?: boolean): void {
    const filters = this.filterService.getFiltersFromSessionStorage(reset);
    this.deviceSettingGroupNameKeyword = filters.deviceSettingGroupName || '';
    this.selectedDeviceType = filters.deviceTypeDisplayName || '';
    this.descriptionKeyword = filters.description || '';
    this.selectedBulkSettingStatus = this.isBulkSettingStatusFilter(
      filters.bulkSettingStatus,
    )
      ? filters.bulkSettingStatus
      : null;
    this.sortState = filters.sortState;
    this.pageIndex = filters.pageIndex;
  }

  /**
   * フィルター条件をセッションストレージに保存する
   */
  private saveFiltersToStorage(): void {
    const filterList: DeviceGroupListFilter = {
      deviceSettingGroupName: this.deviceSettingGroupNameKeyword,
      deviceTypeDisplayName: this.selectedDeviceType,
      description: this.descriptionKeyword,
      bulkSettingStatus: this.selectedBulkSettingStatus,
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }

  /**
   * 一括設定が未適用のデバイス設定グループが存在するか
   */
  hasAnyNotBulkApplied(): boolean {
    return this.deviceSettingGroupList.some((group) =>
      this.isBulkSettingButtonWarning(group),
    );
  }

  /**
   * ステータス列に表示するラベルを取得する。
   * @param group 対象のデバイス設定グループ
   * @returns 現在の一括設定ステータスに対応する表示ラベル
   */
  getBulkSettingStatusLabel(group: DeviceSettingGroupViewModel): string {
    const status = this.getNormalizedBulkSettingStatus(group);
    if (status === 'IN_PROGRESS') {
      const progress = group.bulkSettingProgress ?? 0;
      return `${progress}%`;
    }
    if (status === 'COMPLETED') {
      return '設定済';
    }
    if (status === 'FAILED') {
      return '設定エラー';
    }
    return '未設定';
  }

  /**
   * グループの編集・削除操作を無効化するか判定する。
   * @param group 対象のデバイス設定グループ
   * @returns 操作を無効化する場合は `true`
   */
  isGroupOperationDisabled(group: DeviceSettingGroupViewModel): boolean {
    return group.bulkSettingStatus === 'IN_PROGRESS';
  }

  /**
   * 一括設定ステータスが設定エラーかを判定する
   */
  public isBulkSettingFailed(group: DeviceSettingGroupViewModel): boolean {
    return this.getNormalizedBulkSettingStatus(group) === 'FAILED';
  }

  /**
   * 一括設定ボタンを警告表示にするか判定する。
   * @param group 対象のデバイス設定グループ
   * @returns 未設定/設定エラーかつ契約内デバイスがある場合は `true`
   */
  isBulkSettingButtonWarning(group: DeviceSettingGroupViewModel): boolean {
    return (
      (group.bulkSettingStatus === 'PENDING' ||
        group.bulkSettingStatus === 'FAILED' ||
        group.bulkSettingStatus == null) &&
      !group.isAllContractDevicesBulkApplied
    );
  }

  /**
   * 一括設定ボタンを無効化するか判定する。
   * @param group 対象のデバイス設定グループ
   * @returns 設定中またはデバイス未所属の場合は `true`
   */
  isBulkSettingButtonDisabled(group: DeviceSettingGroupViewModel): boolean {
    return this.isGroupOperationDisabled(group) || group.deviceCount === 0;
  }

  /**
   * `null` / `undefined` のステータスを既定値へ正規化する。
   * @param group 対象のデバイス設定グループ
   * @returns 正規化後の一括設定ステータス
   */
  private getNormalizedBulkSettingStatus(
    group: DeviceSettingGroupViewModel,
  ): BulkSettingStatusFilter {
    return group.bulkSettingStatus ?? 'PENDING';
  }

  /**
   * ステータス列ソート用の数値を返す。
   * @param group 対象のデバイス設定グループ
   * @returns ステータス優先順位と進捗率を反映したソート値
   */
  private getBulkSettingStatusSortValue(
    group: DeviceSettingGroupViewModel,
  ): number {
    const status = this.getNormalizedBulkSettingStatus(group);
    const baseOrder = BULK_SETTING_STATUS_SORT_ORDER[status] * 1000;

    if (status !== 'IN_PROGRESS') {
      return baseOrder;
    }

    return baseOrder + (group.bulkSettingProgress ?? 0);
  }

  /**
   * フィルタ値が有効な一括設定ステータスか判定する。
   * @param status 保存済みフィルタから読み出した値
   * @returns 型ガードの判定結果
   */
  private isBulkSettingStatusFilter(
    status: unknown,
  ): status is BulkSettingStatusFilter {
    return (
      status === 'PENDING' ||
      status === 'IN_PROGRESS' ||
      status === 'COMPLETED' ||
      status === 'FAILED'
    );
  }
}
